import React from 'react'
import { Area, ShiftWithDuty, EventDetailMetaProps } from '../data/types'
import { ShiftsList } from './ShiftsList'
import { EditIcon, DeleteIcon, ChevronUpIcon, ChevronDownIcon, PlusIcon, EyeIcon, EyeHideIcon, DuplicateIcon, DownloadIcon } from '../icons'
import { Button } from './ui/Button'
import { Card } from './ui/Card'

type Props = {
    area: Area
    shifts: ShiftWithDuty[]
    isExpanded: boolean
    onToggle: () => void
    onEdit: () => void
    onDelete: () => void
    onDuplicate?: () => void
    onAddShift: () => void
    onEditShift?: (shift: ShiftWithDuty) => void
    onDeleteShift?: (shift: ShiftWithDuty) => void
    onAssignShift?: (shift: ShiftWithDuty) => void
    onViewShift?: (shift: ShiftWithDuty) => void
    onDuplicateShift?: (shift: ShiftWithDuty) => void
    onCopyShiftAllocations?: (shift: ShiftWithDuty) => void
    onVolunteerAreaClick?: (area: Area) => void
    onExportShiftRegionStatus?: (area: Area) => void
    meta: EventDetailMetaProps
    formatTime: (timeStr: string | null | undefined) => string
    canEdit?: boolean
    canDelete?: boolean
    canDuplicate?: boolean
    canCreateShift?: boolean
    canEditShift?: (shift: ShiftWithDuty) => boolean
    canDeleteShift?: (shift: ShiftWithDuty) => boolean
    canViewAllocations?: boolean
    canViewShiftAllocations?: (shift: ShiftWithDuty) => boolean
    canManageAllocations?: (shift: ShiftWithDuty) => boolean
    canDuplicateShift?: (shift: ShiftWithDuty) => boolean
    canCopyShiftAllocations?: (shift: ShiftWithDuty) => boolean
    isExportingShiftRegionStatus?: boolean
    eventShiftsVisible?: boolean
    showVisibility?: boolean
    [x: string]: any;
}

export const AreaCard = (props: Props) => {
    const { area, shifts, isExpanded, onToggle, onEdit, onDelete, onDuplicate, onAddShift, onEditShift, onDeleteShift, onAssignShift, onViewShift, onDuplicateShift, onCopyShiftAllocations, meta, formatTime, onVolunteerAreaClick, onExportShiftRegionStatus, canEdit, canDelete, canDuplicate, canCreateShift, canEditShift, canDeleteShift, canViewAllocations, canViewShiftAllocations, canManageAllocations, canDuplicateShift, canCopyShiftAllocations, isExportingShiftRegionStatus, eventShiftsVisible, showVisibility } = props

    const totalAreaRequiredVolunteers = React.useMemo(() => {
        return (shifts || []).reduce((sum, shift) => sum + (Number(shift.required_volunteer_count || 0)), 0);
    }, [shifts]);


    return (
        <Card>
            <div className="p-6" style={{ borderColor: area.access_color?.color, borderWidth: 5 }}>
                <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                {area.name}
                            </h3>
                            {/* Visibility indicator - only shown to admins */}
                            {showVisibility && (
                                <div
                                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                        area.shifts_visible
                                            ? 'bg-success-100 text-success-700 dark:bg-success-900/50 dark:text-success-300'
                                            : eventShiftsVisible
                                                ? 'bg-success-100 text-success-700 dark:bg-success-900/50 dark:text-success-300'
                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                    }`}
                                    title={
                                        area.shifts_visible
                                            ? meta.shiftsVisibleTooltip
                                            : eventShiftsVisible
                                                ? meta.shiftsVisibleViaEventTooltip
                                                : meta.shiftsHiddenTooltip
                                    }
                                >
                                    {area.shifts_visible ? (
                                        <><EyeIcon className="w-3.5 h-3.5" /> {meta.visibleLabel}</>
                                    ) : eventShiftsVisible ? (
                                        <><EyeIcon className="w-3.5 h-3.5" /> {meta.visibleViaEventLabel}</>
                                    ) : (
                                        <><EyeHideIcon className="w-3.5 h-3.5" /> {meta.hiddenLabel}</>
                                    )}
                                </div>
                            )}
                        </div>

                        {/*Display Lead details*/}

                        <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">

                            {/* Lead Row */}
                            <div className="flex items-center gap-6">
                                <span>
                                    <span className="font-semibold">{meta.leadNameTitle}:</span>{" "}
                                    {area.lead ? `${area.lead.first_name} ${area.lead.last_name}` : "Unassigned"}
                                </span>

                                <span>
                                    <span className="font-semibold">{meta.contactTitle}:</span>{" "}
                                    {area.lead ? area.lead.phone : "Unavailable"}
                                </span>
                            </div>

                            {/* Deputy Lead Row */}
                            <div className="flex items-center gap-6">
                                <span>
                                    <span className="font-semibold">{meta.deputyLeadNameTitle}:</span>{" "}
                                    {area.deputy_lead ? `${area.deputy_lead.first_name} ${area.deputy_lead.last_name}` : "Unassigned"}
                                </span>

                                <span>
                                    <span className="font-semibold">{meta.contactTitle}:</span>{" "}
                                    {area.deputy_lead ? area.deputy_lead.phone : "Unavailable"}
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="font-semibold">{meta.shiftsTitle}: </span> {shifts?.length}
                            </div>

                            {/* Volunteer Count Requirements */}

                            <div className='flex gap-6  pt-3'>
                                <div>
                                    <span className="font-semibold">{meta.required_volunteer_count_label}: </span>
                                    {totalAreaRequiredVolunteers || 0}
                                </div>
                                <div>
                                    <span className="font-semibold">{meta.allocation_count_label}: </span>
                                    {area.allocation_count || 0}
                                    {(area.total_duplications || 0) > 0 && (
                                        <span className="text-gray-500 ms-1">(+{area.total_duplications} duplications)</span>
                                    )}
                                </div>

                                <div>
                                    <span className="font-semibold">{meta.multi_allocation_count_label}: </span>
                                    {area.multi_allocation_count || 0}
                                </div>
                            </div>

                            {/* Allocation Status Counts */}

                            <div className='flex gap-6  pt-3'>
                                <div>
                                    <span className="font-semibold">{meta.accepted_allocations_label}: </span>
                                    {area.accepted_count || 0}
                                </div>

                                <div>
                                    <span className="font-semibold">{meta.rejected_allocations_label}: </span>
                                    {area.rejected_count || 0}
                                </div>

                                <div>
                                    <span className="font-semibold">{meta.pending_allocations_label}: </span>
                                    {area.pending_count || 0}
                                </div>

                                <div>
                                    <span className="font-semibold">{meta.leads_count_label}: </span>
                                    {area.leads_count || 0}
                                </div>

                            </div>


                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">

                        {/* Top actions */}
                        <div className="flex items-center gap-2">
                            {canEdit && (
                                <Button onClick={onEdit} variant="icon" title="Edit area">
                                    <EditIcon className="w-5 h-5" />
                                </Button>
                            )}

                            {canDelete && (
                                <Button onClick={onDelete} variant="icon" color="danger" title="Delete area">
                                    <DeleteIcon className="w-5 h-5" />
                                </Button>
                            )}

                            {canDuplicate && onDuplicate && (
                                <Button onClick={onDuplicate} variant="icon" color="primary" title="Duplicate area">
                                    <DuplicateIcon className="w-5 h-5" />
                                </Button>
                            )}

                            <button
                                onClick={onToggle}
                                className="ms-2 flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
                            >
                                <span className="text-sm">
                                    {isExpanded ? meta.hideShiftsButton : meta.showShiftsButton}
                                </span>
                                {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                            </button>
                        </div>

                        {canViewAllocations && (onVolunteerAreaClick || onExportShiftRegionStatus) && (
                            <div className="mt-9 pt-4 flex flex-wrap justify-end gap-2">
                                {onVolunteerAreaClick && (
                                    <Button type="button" color="primary" onClick={() => onVolunteerAreaClick(area)}>
                                        <EyeIcon />
                                        Show Area Allocations
                                    </Button>
                                )}
                                {onExportShiftRegionStatus && (
                                    <Button type="button" color="primary" variant="outline" onClick={() => onExportShiftRegionStatus(area)} disabled={isExportingShiftRegionStatus}>
                                        <DownloadIcon />
                                        {isExportingShiftRegionStatus ? meta.exportingShiftRegionStatusButton : meta.exportShiftRegionStatusButton}
                                    </Button>
                                )}
                            </div>
                        )}

                    </div>

                </div>

                {isExpanded && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                {meta.shiftsTitle}
                            </h4>
                            {canCreateShift && (
                                <Button onClick={onAddShift} color="success" size="sm">
                                    <PlusIcon className="w-4 h-4" />
                                    {meta.addShiftButton}
                                </Button>
                            )}
                        </div>
                        <ShiftsList
                            shifts={shifts}
                            meta={meta}
                            formatTime={formatTime}
                            onEdit={onEditShift}
                            onDelete={onDeleteShift}
                            onAssign={onAssignShift}
                            onView={onViewShift}
                            onDuplicate={onDuplicateShift}
                            onCopyAllocations={onCopyShiftAllocations}
                            canEdit={canEditShift}
                            canDelete={canDeleteShift}
                            canView={canViewShiftAllocations}
                            canAssign={canManageAllocations}
                            canDuplicate={canDuplicateShift}
                            canCopyAllocations={canCopyShiftAllocations}
                            areaShiftsVisible={area.shifts_visible}
                            eventShiftsVisible={eventShiftsVisible}
                            showVisibility={showVisibility}
                        />
                    </div>
                )}
            </div>

        </Card>
    )
}
